<template>
  <section class="tb-card summary">
    <header class="tb-card__head">
      <p class="tb-card__title">Current game</p>
      <span class="tb-pill" :class="statusClass">
        <span v-if="isLive" class="tb-pill__dot"></span>
        {{ statusLabel }}
      </span>
    </header>

    <div class="tb-card__body">
      <div class="summary__when">
        <strong>{{ scheduleLabel }}</strong>
        <small>{{ relativeLabel }}</small>
      </div>

      <div class="summary__progress">
        <div class="summary__progress-head">
          <span>Tickets sold</span>
          <strong>{{ sold }} / {{ limit }}</strong>
        </div>
        <div class="summary__bar">
          <span :style="{ width: `${soldPercent}%` }"></span>
        </div>
        <small>{{ soldPercent }}% sold &middot; {{ available }} still available</small>
      </div>

      <dl class="summary__facts">
        <div class="summary__fact">
          <dt>Price / ticket</dt>
          <dd>{{ game.per_ticket }}</dd>
        </div>
        <div class="summary__fact">
          <dt>Commission / ticket</dt>
          <dd>{{ game.commission }}</dd>
        </div>
        <div class="summary__fact">
          <dt>Collection so far</dt>
          <dd>{{ collection }}</dd>
        </div>
        <div class="summary__fact">
          <dt>Your sales</dt>
          <dd>{{ mySold }}</dd>
        </div>
        <div class="summary__fact">
          <dt>Your earning</dt>
          <dd>{{ myEarning }}</dd>
        </div>
        <div v-if="sheetCount" class="summary__fact">
          <dt>Sheets booked</dt>
          <dd>{{ sheetCount }}</dd>
        </div>
        <div v-if="isLive" class="summary__fact">
          <dt>Numbers called</dt>
          <dd>{{ polledCount }} / 90</dd>
        </div>
        <div class="summary__fact summary__fact--wide">
          <dt>Game id</dt>
          <dd class="summary__mono">{{ gameId }}</dd>
        </div>
      </dl>

      <div class="summary__claims">
        <span class="tb-card__title">Claims in play ({{ claims.length }})</span>
        <div v-if="claims.length" class="summary__chips">
          <span v-for="claim in claims" :key="claim" class="tb-pill">{{ label(claim) }}</span>
        </div>
        <p v-else class="tb-empty">No claims are enabled for this game.</p>
      </div>
    </div>
  </section>
</template>

<script>
  import moment from "moment";
  import { winnerLabel } from "../constants/game";

  /**
   * At-a-glance state of the game an operator is working on: when it runs, how
   * much of it is sold, what it is worth, and which claims are in play.
   */
  export default {
    name: "GameSummary",
    props: {
      // The Firestore document data, not the snapshot.
      game: {
        type: Object,
        required: true
      },
      gameId: {
        type: String,
        default: ""
      },
      sold: {
        type: Number,
        default: 0
      },
      mySold: {
        type: Number,
        default: 0
      },
      // Enabled claim keys, e.g. ["fullhouse", "top_line"].
      claims: {
        type: Array,
        default: () => []
      },
      sheetCount: {
        type: Number,
        default: 0
      },
      polledCount: {
        type: Number,
        default: 0
      }
    },
    computed: {
      isLive() {
        return this.game.status === "live";
      },
      statusLabel() {
        if (this.game.status === "winner_declared") return "Finished";
        if (this.isLive) return "Live";
        return this.game.status;
      },
      statusClass() {
        if (this.isLive) return "tb-pill--live";
        if (this.game.status === "winner_declared") return "tb-pill--over";
        return "";
      },
      scheduleMoment() {
        const schedule = this.game.schedule;
        if (schedule && typeof schedule.seconds === "number") {
          return moment(schedule.seconds * 1000);
        }
        return moment(schedule || undefined);
      },
      scheduleLabel() {
        return this.scheduleMoment.format("ddd, MMM D YYYY [at] hh:mm A");
      },
      relativeLabel() {
        return this.scheduleMoment.fromNow();
      },
      limit() {
        return this.game.limit || 0;
      },
      available() {
        return Math.max(0, this.limit - this.sold);
      },
      soldPercent() {
        if (!this.limit) return 0;
        return Math.round((this.sold / this.limit) * 100);
      },
      collection() {
        return this.sold * (this.game.per_ticket || 0);
      },
      myEarning() {
        return this.mySold * (this.game.commission || 0);
      }
    },
    methods: {
      label: winnerLabel
    }
  };
</script>

<style scoped>
  .summary__when {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    margin-bottom: 14px;
    font-size: clamp(14px, 4vw, 16px);
  }

  .summary__when small {
    color: var(--tb-ink-soft);
    font-size: 12px;
    font-weight: 400;
  }

  .summary__progress-head {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 13px;
  }

  .summary__bar {
    height: 8px;
    margin: 6px 0 4px;
    border-radius: 999px;
    background: var(--tb-surface-alt);
    overflow: hidden;
  }

  .summary__bar span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--tb-called);
    transition: width 0.4s ease;
  }

  .summary__progress small {
    color: var(--tb-ink-soft);
    font-size: 12px;
    font-weight: 400;
  }

  .summary__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin: 16px 0 0;
  }

  .summary__fact {
    padding: 8px 10px;
    border-radius: 10px;
    background: var(--tb-surface-alt);
  }

  .summary__fact--wide {
    grid-column: 1 / -1;
  }

  .summary__fact dt {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--tb-ink-soft);
  }

  .summary__fact dd {
    margin: 2px 0 0;
    font-size: clamp(15px, 4.5vw, 19px);
    font-variant-numeric: tabular-nums;
  }

  .summary__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px !important;
    overflow-wrap: anywhere;
  }

  .summary__claims {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid var(--tb-line);
  }

  .summary__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
</style>
